import {css, CSSResult, html, notEqual, nothing} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {combineLatest, filter, map, of, scan, tap} from 'rxjs';

import Decimal from './break_infinity';
import {Panel} from './panel';
import {ri} from './runner-idle';
import {formatDistance, formatEng, formatPosition} from './utils';
import {Vector} from './Vector';
import {__, compose, equals, gt, gte, join, length, not} from 'ramda';

@customElement('ri-landmarks')
export class Landmarks extends Panel {
  // #region Properties (7)

  public static override styles: CSSResult[] = [
    ...Panel.styles,
    css`
      button.target-set {
        color: green;
        outline: 2px green solid;
      }
      button.target-unset {
        color: red;
        outline: 2px red solid;
      }

      .position-sidebar {
        width: 25%;
      }
      .landmark-selector {
        width: 50%;
      }
      #map-extra-info {
        /* width: 25%; */
      }
      select {
        max-width: 24ch;
      }
      option {
        font-weight: bolder;
      }
    `,
  ];

  @state() public distance: Decimal = new Decimal();
  @state() public position: Vector = new Vector();
  @state() public targetPosition: Vector = new Vector();

  public override helpContent = `This is the <strong>Map Info</strong> panel.
  <br>Info about the <strong>Map</strong> is displayed here,
  <br>and you can change your target position directly.
  <br>Whenever you move, you will move towards your target position.
  
  `;
  public override id = 'landmarks';
  public override name = 'Targeting';
  positionTags = '';
  speedFactor: Decimal = new Decimal(1);

  // #endregion Properties (7)

  // #region Public Methods (3)

  public override render() {
    return super.render(html`
      <div class="flex-vertical">
        <div class="flex-horizontal">
          <div class="flex-vertical position-sidebar">
            <!-- <div>
            Total Distance:
            <span class="fancy distance">
              ${formatEng(ri.distance.valueOf())}
            </span>
            m
            </div> -->
            <div>
              Current Position:<br />
              <span class="fancy distance">
                ${formatPosition(ri.position)}
              </span>
            </div>
            <div>
              Target Position:<br />
              <div class="flex-horizontal">
                <input
                  id="targetX-input"
                  value=${this.targetPosition.x.valueOf()}
                  class="fancy distance position-input"
                  @change=${(event: Event) => {
                    if (!(event.target instanceof HTMLInputElement)) return;
                    try {
                      const x = new Decimal(event.target.value);
                      if (x.neq(ri.targetPosition.x)) {
                        ri.targetPosition = new Vector(x, ri.targetPosition.y);
                      }
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                />
                <input
                  id="targetY-input"
                  value=${this.targetPosition.y.valueOf()}
                  class="fancy distance position-input"
                  @change=${(event: Event) => {
                    if (!(event.target instanceof HTMLInputElement)) return;
                    try {
                      const y = new Decimal(event.target.value);
                      if (y.neq(ri.targetPosition.y)) {
                        ri.targetPosition = new Vector(ri.targetPosition.x, y);
                      }
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                />
              </div>
            </div>
            ${ri.targetPosition.distance(ri.position).gt(0)
              ? html`
                  <div>
                    Distance to Target:
                    <span class="fancy distance">
                      ${formatEng(ri.targetPosition.distance(ri.position))}m
                    </span>
                  </div>
                `
              : html`<div>At Target</div>`}
          </div>
          <!-- <div class="landmark-selector">
            <select id="destination-select" @input=${this.updateNextLandmark}>
              ${Object.values(ri.landmarks)
            .filter(
              (landmark) =>
                landmark.navigatable &&
                landmark.visible &&
                !landmark.temporaryFinished$.value
            )
            .map(
              (landmark) =>
                html`<option
                  value=${landmark.name}
                  ?selected=${ri.nextLandmark === landmark}
                >
                  ${landmark.displayName}:
                  ${formatDistance(
                    landmark.shape.nearestPointTo(ri.position),
                    ri.position
                  )}
                  ${landmark.visited ? '' : 'Unvisited'}
                </option>`
            )}
            </select>
            <button
              class="${ri.nextLandmark?.containsPoint(ri.targetPosition)
            ? 'target-set'
            : 'target-unset'}"
              @click=${() => {
            ri.hunting$.next(false);
            ri.updateNextLandmark();
            // Set destination
            ri.targetPosition =
              ri.nextLandmark?.shape.nearestPointTo(ri.position) ??
              ri.targetPosition;
            const clickXEl = ri.shadowRoot?.getElementById('targetX-input');
            if (clickXEl instanceof HTMLInputElement)
              clickXEl.value = String(ri.targetPosition[0].toNumber());
            const clickYEl = ri.shadowRoot?.getElementById('targetY-input');
            if (clickYEl instanceof HTMLInputElement)
              clickYEl.value = String(ri.targetPosition[1].toNumber());
            ri.requestUpdate();
          }}
            >
              ${ri.nextLandmark?.containsPoint(ri.targetPosition)
            ? `Destination Set`
            : `Set Destination`}
            </button>
            <div>
              Distance:
              ${formatEng(
            ri.nextLandmark?.distance(ri.position) ?? new Decimal(0)
          )}m
            </div>
            <div>${ri.nextLandmark?.description}</div>
            <div>${ri.nextLandmark?.shape.toString()}</div>
          </div> -->
          <!-- <button
          @click=${() => {
            ri.hunting$.next(true);
          }}
        >
          Hunt
        </button> -->
          <div id="map-extra-info" class="flex-vertical flex-stretch">
            <div>
              Grid Size: <span id="grid-size">${ri.gridSize ?? 0}</span>m
            </div>
            <div>Vision Range: <span>${formatEng(ri.visionRange)}</span>m</div>
            <div>Tags: <span>${this.positionTags}</span></div>
            ${this.speedFactor.neq(1)
              ? html`<div>Speed: <span>${this.speedFactor}</span></div>`
              : nothing}
          </div>
        </div>
      </div>
    `);
  }

  public override setup() {
    super.setup();
    this.subscribe('distance', ri.distance$);
    this.subscribe('position', ri.position$);
    this.subscribe(
      'targetPosition',
      ri.targetPositions$.pipe(
        filter(compose(gte(__, 1), length)),
        map(([targetPosition]) => targetPosition),
        tap((targetPosition) => {
          const targetXInput = this.shadowRoot?.getElementById('targetX-input');
          const targetYInput = this.shadowRoot?.getElementById('targetY-input');
          if (
            targetXInput instanceof HTMLInputElement &&
            targetYInput instanceof HTMLInputElement
          ) {
            targetXInput.value = targetPosition.x.toString();
            targetYInput.value = targetPosition.y.toString();
          }
        })
      )
    );
    this.subscribe(
      'positionTags',
      combineLatest(
        Object.values(ri.landmarks)
          .sort((a, b) => {
            return a.z.minus(b.z).toNumber();
          })
          .map((landmark) =>
            combineLatest([
              of(Array.from(landmark.tags)),
              landmark.containsPlayer$,
            ])
          )
      ).pipe(
        map((landmarks) =>
          landmarks
            .filter(([, containsPlayer]) => containsPlayer)
            .map(([tags]) =>
              tags.filter((tag) => tag && tag !== 'landmark').flat()
            )
            .join(', ')
        )
      )
    );
    this.subscribe(
      'speedFactor',
      combineLatest(
        Object.values(ri.landmarks)
          .sort((a, b) => {
            return a.z.minus(b.z).toNumber();
          })
          .map((landmark) =>
            combineLatest([of(landmark.speed), landmark.containsPlayer$])
          )
      ).pipe(
        map((landmarks) =>
          landmarks
            .filter(([, containsPlayer]) => containsPlayer)
            .reduce((acc, [speed]) => acc.times(speed), new Decimal(1))
        )
      )
    );
  }

  public updateNextLandmark() {
    ri.updateNextLandmark();
    this.requestUpdate();
  }

  // #endregion Public Methods (3)
}
